import { Box, CircularProgress, Typography } from '@mui/material'
import { Fragment, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Context from '../Context'
import theme from '../theme'
import { FONT, cellSx, headSx } from './board'
import { MAX_EVENTS } from './BoardFilters'
import { useClock } from '../utils/clock'
import { pathForEvent } from '../utils/routes'
import { findSoftRow, findSharpRow, displayBookmaker } from '../utils/arb'
import { formatDecimalPrice } from '../utils/price'
import Favorite from './Favorite'

/**
 * One line per fixture, in the order they kick off, priced as our own book prices it. Where the search
 * board asks "where are we out of line", this one asks "what is coming up and what are we showing" — so
 * the other books stay folded away until the arrow is clicked, and then every book that prices the game
 * is laid out underneath in the same columns.
 */

// a fixture is one row here rather than seven, so the cap can be far lower and still show far more games
const MAX_FIXTURES = 400

/**
 * The column groups, in the order the league grid prints them, tied to the market chips: pressing AH
 * leaves the handicap columns and takes the rest away.
 */
const GROUPS = [
  {
    market: '1X2',
    columns: [
      { head: 'Home', title: 'Price on the home team', price: 'homePrice' },
      { head: 'Draw', title: 'Price on the draw', price: 'drawPrice' },
      { head: 'Away', title: 'Price on the away team', price: 'awayPrice' }
    ]
  },
  {
    market: 'AH',
    columns: [
      // pick'em is a real handicap and the commonest one in soccer: it is only missing when the prices are
      { head: 'SP', title: 'The handicap, in points, for the home team', points: 'spreadPoints', pair: ['homeSpreadPrice', 'awaySpreadPrice'] },
      { head: 'SPH', title: 'Price on the home team with that handicap', price: 'homeSpreadPrice' },
      { head: 'SPA', title: 'Price on the away team with that handicap', price: 'awaySpreadPrice' }
    ]
  },
  {
    market: 'AOU',
    columns: [
      { head: 'Total', title: 'The total line: how many goals or points the book expects', points: 'totalPoints', pair: ['overPrice', 'underPrice'] },
      { head: 'Under', title: 'Price on the match finishing below the total', price: 'underPrice' },
      { head: 'Over', title: 'Price on the match finishing above the total', price: 'overPrice' }
    ]
  }
]

const usable = (price) => typeof price === 'number' && price > 1

const eventKey = (event) => `${event.home}|${event.away}|${event.koTime}`

/**
 * A row per fixture: what is coming up and what the sharp book makes of it, with every other book folded
 * away behind the arrow. The wide layout — markets across the row rather than down it.
 */
function FixturesTable({ events, error, filters }) {
  const { usPrices } = useContext(Context)
  const navigate = useNavigate()
  const clock = useClock()

  const { wantsMarket } = filters

  // which fixtures have been opened up, by home|away|kick off so a poll cannot close them again
  const [ open, setOpen ] = useState(() => new Set())
  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    return next
  })

  const groups = GROUPS.filter((group) => wantsMarket(group.market))
  const columns = groups.flatMap((group) => group.columns)

  const fixtures = useMemo(() => {
    const out = []

    for (const event of events || []) {

      /**
       * The line on show is the sharp one: what a fixture is worth is Pinnacle's answer, and our own
       * price is one of the books folded away underneath. A fixture Pinnacle does not price falls back
       * to ours rather than disappearing — it is still a game that is about to start.
       */
      const books = event.bookieList || []
      const headline = findSharpRow(books) || findSoftRow(books) || books[0]
      if (!headline) {
        continue
      }

      out.push({
        key: eventKey(event),
        home: event.home,
        away: event.away,
        koTime: event.koTime,
        fav: Boolean(event.fav),
        sport: event.sport,
        league: event.league,
        headline,
        books
      })
    }

    out.sort((a, b) => a.koTime - b.koTime || a.home.localeCompare(b.home))
    return out
    // eslint-disable-next-line
  }, [events, filters.sport, filters.leagues, filters.teams, filters.markets, filters.within, filters.favOnly])

  // a book's cell: a price is a price, a handicap or a total is just the number it is
  const valueOf = (row, column) => {
    if (!row) {
      return '—'
    }
    if (column.points) {
      const points = row[column.points]
      if (points === null || points === undefined) {
        return '—'
      }
      // a zero with prices beside it is the pick'em line; a zero with none is the book not pricing it
      if (points === 0 && !(column.pair || []).some((field) => usable(row[field]))) {
        return '—'
      }
      return points
    }
    return usable(row[column.price]) ? formatDecimalPrice(row[column.price], usPrices) : '—'
  }

  return (
    <Box>
      <Box>
        {error && (
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>{error}</Typography>
        )}

        {events === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><CircularProgress size={26} /></Box>
        )}

        {events !== null && (
          <Box>
            {(fixtures.length > MAX_FIXTURES || (events || []).length >= MAX_EVENTS) && (
              <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', paddingBottom: '10px' }}>
                Showing the games that start soonest. Narrow the filters for the rest.
              </Typography>
            )}

            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...headSx, width: '28px' }} />
                  <Box component="th" sx={headSx}>Kick off ({clock.label})</Box>
                  <Box component="th" sx={headSx}>Game</Box>
                  <Box component="th" sx={headSx}>Competition</Box>
                  <Box component="th" sx={headSx}>Book</Box>
                  {columns.map((column) => (
                    <Box component="th" key={column.head} sx={headSx} title={column.title}>{column.head}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {fixtures.slice(0, MAX_FIXTURES).map((fixture) => {
                  const isOpen = open.has(fixture.key)
                  // every book that prices this game, ours first, when the arrow has been clicked
                  const rest = isOpen ? fixture.books.filter((row) => row !== fixture.headline) : []
                  const topSx = { ...cellSx, borderTop: '2px solid #DEDEE4' }
                  /**
                   * One keyed fragment per fixture rather than an array of rows: an array inside the map
                   * is keyed by position, so the fixture that kicks off and leaves the top of the board
                   * takes every row below it with it — focus, selection and open panels included.
                   */
                  return (
                  <Fragment key={fixture.key}>
                    <Box
                      component="tr"
                      sx={{ '&:hover': { backgroundColor: '#F8F8FA' } }}
                    >
                      <Box component="td" sx={{ ...topSx, padding: '6px 0 6px 6px' }}>
                        <Box
                          component="button"
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Hide the other books' : 'Show the other books'}
                          onClick={() => toggle(fixture.key)}
                          sx={{
                            appearance: 'none',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '2px 4px',
                            color: '#9A9EA3',
                            fontSize: '11px',
                            lineHeight: 1,
                            transform: isOpen ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                            '&:hover': { color: theme.palette.primary.light }
                          }}
                        >
                          ▶
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...topSx, color: '#6E7378', fontVariantNumeric: 'tabular-nums' }}>
                        {clock.kickOff(fixture.koTime)}
                      </Box>
                      <Box component="td" sx={{ ...topSx, fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Favorite compact event={fixture} />
                          <Box
                            component="span"
                            onClick={() => navigate(`${pathForEvent(fixture)}?book=pinnacle`)}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: theme.palette.primary.light } }}
                          >
                            {fixture.home} v {fixture.away}
                          </Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...topSx, color: '#9A9EA3' }}>
                        {fixture.sport} · {fixture.league}
                      </Box>
                      <Box component="td" sx={{ ...topSx, color: '#6E7378' }}>
                        {displayBookmaker(fixture.headline.bookmaker)}
                      </Box>
                      {columns.map((column) => (
                        <Box
                          component="td"
                          key={column.head}
                          sx={{ ...topSx, fontWeight: column.points ? 400 : 700, color: column.points ? '#6E7378' : 'inherit', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {valueOf(fixture.headline, column)}
                        </Box>
                      ))}
                    </Box>
                    {rest.map((row, index) => {
                      // an exchange lay price is not comparable with the back prices stacked above it
                      const lay = /lay/i.test(String(row.bookmaker || ''))
                      return (
                      <Box component="tr" key={`${fixture.key}|${row.bookmaker}|${index}`} sx={{ backgroundColor: '#FBFBFC' }}>
                        <Box component="td" sx={cellSx} />
                        <Box component="td" sx={cellSx} />
                        <Box component="td" sx={cellSx} />
                        <Box component="td" sx={cellSx} />
                        <Box component="td" sx={{ ...cellSx, color: lay ? '#B4B8BC' : '#6E7378' }}>{displayBookmaker(row.bookmaker)}</Box>
                        {columns.map((column) => (
                          <Box
                            component="td"
                            key={column.head}
                            title={lay ? 'A lay price: what it costs to take the other side, not a back price' : undefined}
                            sx={{
                              ...cellSx,
                              color: lay ? '#B4B8BC' : (column.points ? '#9A9EA3' : '#3C4043'),
                              fontStyle: lay ? 'italic' : 'normal',
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            {valueOf(row, column)}
                          </Box>
                        ))}
                      </Box>
                    )})}
                  </Fragment>
                  )
                })}
              </Box>
            </Box>

            {fixtures.length === 0 && (
              <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#9A9EA3', padding: '24px 0' }}>
                Nothing matches these filters.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FixturesTable
